import {withProps} from 'recompose'

import {H3} from '@jetbrains/ring-ui/components/heading/heading'
import classNames from 'classnames'
import {differenceInMonths} from 'date-fns/differenceInMonths'
import {parseISO} from 'date-fns/parseISO'
import compose from 'lodash/flowRight'
import type {ReactNode} from 'react'
import {memo, useMemo} from 'react'
import type {MeasuredComponentProps} from 'react-measure'
import {graphql, useFragment} from 'react-relay'

import EntityLink from '../../../../../../containers/EntityLink'
import {withContentRect} from '../../../../../../containers/Measure'
import withDerivedState from '../../../../../../hocs/withDerivedState'
import useTimeSelector from '../../../../../../hooks/useTimeSelector'
import BuildInfo from '../../../../../common/BuildInfo/BuildInfo.container'
import BuildsTimeline from '../../../../../common/BuildsTimeline/BuildsTimeline.container'
import OverviewBuildTypeActions from '../../../../../common/BuildTypeActionsDropdown/BuildTypeActionsDropdown'
import FavoriteStar from '../../../../../common/FavoriteStar/FavoriteStar.container'
import OverviewStatusIcon from '../../../../../common/ProjectOrBuildTypeIcon/OverviewStatusIcon.container'
import {EntityIconType} from '../../../../../common/ProjectOrBuildTypeIcon/ProjectOrBuildTypeIcon.types'
import RunBuildInSelectedBranch from '../../../../../common/RunBuild/RunBuildInSelectedBranch.container'
import OverviewSystemProblems from '../../../../BuildTypePage/BuildTypePageHeader/OverviewSystemProblems/OverviewSystemProblems.container'

import {buildTypeInfoPanelHint} from './BuildTypeInfoPanel.hint'
import type {
  NameProps,
  DerivedStateProps,
  NameOwnProps,
  BuildTypeInfoPanelProps,
} from './BuildTypeInfoPanel.types'
import BuildTypeStatus from './BuildTypeStatus/BuildTypeStatus.container'

import styles from './BuildTypeInfoPanel.css'

export const TIMELINE_STAT_COUNT = 14
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const COLLAPSED_BUILD_INFO_HEIGHT = 8 * 8
const LINE_HEIGHT = 18
const OFFSET = 3
const Name = compose(
  withContentRect(['scroll', 'client']),
  withProps(({contentRect: {scroll, client}}: MeasuredComponentProps) => ({
    hasOverflow:
      scroll != null && client != null && scroll.height - client.height >= LINE_HEIGHT - OFFSET,
  })),
  withDerivedState<NameOwnProps, DerivedStateProps>(
    {
      prevName: null,
      moveActions: false,
    },
    ({name, hasOverflow}, {prevName, moveActions}) => {
      if (name !== prevName) {
        return {
          prevName: name,
          moveActions: false,
        }
      }

      if (!moveActions && hasOverflow) {
        return {
          moveActions: true,
        }
      }

      return null
    },
  ),
)(function Name({
  measureRef,
  hasOverflow,
  name,
  buildTypeId,
  archivedProject,
  moveActions,
}: NameProps) {
  const actions = (
    <span
      className={classNames(styles.actionsWrapper, {
        [styles.moveActions]: moveActions,
      })}
    >
      {!archivedProject && (
        <FavoriteStar
          buildTypeId={buildTypeId}
          className={styles.star}
          emptyClassName={styles.emptyStar}
        />
      )}
      <OverviewBuildTypeActions
        compact
        withEdit
        buildTypeId={buildTypeId}
        anchorClassName={styles.actions}
      />
    </span>
  )
  return (
    <>
      <div
        ref={measureRef}
        className={classNames(styles.name, {
          [styles.hasOverflow]: hasOverflow,
        })}
      >
        <div className={styles.spacer}>{'\u200B'}</div>
        <EntityLink title={name} buildTypeId={buildTypeId} className={styles.link}>
          <OverviewStatusIcon
            className={styles.icon}
            type={EntityIconType.BUILD_TYPE}
            id={buildTypeId}
            showPausedInStatus
            showInvestigationInStatus
          />
          {name}
        </EntityLink>
        {!moveActions && actions}
      </div>
      {moveActions && actions}
    </>
  )
})

const fragment = graphql`
  fragment BuildTypeInfoPanelFragment on Builds {
    build {
      rawId
      startDate
      state
    }
    ...BuildsTimelineFragment
  }
`

function BuildTypeInfoPanel({
  className,
  name,
  buildTypeId,
  archivedProject,
  branch,
  contentRect,
  hoveredBuildId,
  selectedBuildId,
  selectBuild,
  measureRef,
  hover,
  handleMouseEnter,
  handleHoverBuild,
  handleMouseLeave,
  handleMouseMove,
  queuedCount,
  loading,
  buildsKey = null,
}: BuildTypeInfoPanelProps) {
  const buildsData = useFragment(fragment, buildsKey)
  const stats = buildsData?.build ?? []

  const {rawId: lastBuildId, startDate: lastBuildStarted} = stats[0] || {}
  const noBuilds = lastBuildId == null && queuedCount === 0 && !loading

  const displayedBuildId = hoveredBuildId ?? selectedBuildId ?? lastBuildId
  const buildInfoHeight = contentRect.scroll?.height ?? COLLAPSED_BUILD_INFO_HEIGHT
  const overflow = buildInfoHeight - COLLAPSED_BUILD_INFO_HEIGHT
  const outdated = useTimeSelector(
    now => lastBuildStarted != null && differenceInMonths(now, parseISO(lastBuildStarted)) >= 1,
  )
  const title = useMemo(
    () => (
      <H3 className={styles.title}>
        <Name name={name} buildTypeId={buildTypeId} archivedProject={archivedProject} />
        <div className={styles.run}>
          <RunBuildInSelectedBranch
            buildTypeId={buildTypeId}
            className={styles.runGroup}
            buttonClassName={styles.runButton}
            splitButtonClassName={styles.runSplitButton}
          />
        </div>
      </H3>
    ),
    [archivedProject, buildTypeId, name],
  )
  const expandable = !loading && !noBuilds
  return (
    <div className={className}>
      <div // tabIndex needed for basic touch support
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={expandable ? 0 : -1}
        className={classNames(styles.card, {
          [styles.expandable]: expandable,
          [styles.expanded]: overflow >= 1,
          [styles.hover]: hover,
        })}
        style={{
          bottom: -overflow,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        data-test="buildtype-card"
        data-buildtype-id={buildTypeId}
        data-hint-container-id={buildTypeInfoPanelHint.id}
      >
        <div className={styles.buildTypeInfo} onMouseEnter={handleMouseEnter}>
          {title}
          <OverviewSystemProblems buildTypeId={buildTypeId} branch={branch} />
          {noBuilds && <div className={styles.noBuilds}>{'No running or finished builds'}</div>}
          <BuildTypeStatus
            outdated={outdated}
            buildTypeId={buildTypeId}
            running={stats.filter(stat => stat.state === 'running').length}
          />
          <div className={styles.charts}>
            <BuildsTimeline
              buildsKey={buildsData}
              loading={loading}
              statCount={TIMELINE_STAT_COUNT}
              currentBuildId={displayedBuildId}
              hoveredBuildId={displayedBuildId}
              hoverBuild={handleHoverBuild}
              selectBuild={selectBuild}
            />
          </div>
        </div>
        {displayedBuildId != null && (
          <div className={styles.buildInfo}>
            <BuildInfo
              key={displayedBuildId}
              elementRef={measureRef}
              buildId={displayedBuildId}
              withFetchBuildData
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default memo(BuildTypeInfoPanel)
export function BuildTypesInfoTableContainer({
  children,
  childCount = 0,
}: {
  children: ReactNode
  childCount?: number
}) {
  return (
    <div className={styles.wrapper}>
      {children}
      {/*
     A hack to prevent last row items to grow wider than others
     We should use CSS Grid instead, but there's an issue with max-width:
       https://stackoverflow.com/questions/54446031/how-to-limit-column-widths-when-using-repeatautofill
    */}
      {Array(childCount)
        .fill(undefined)
        .map((_, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className={styles.placeholder} />
        ))}
    </div>
  )
}
